import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getArticle, getRelatedArticles, type Article } from "@/data/articles";
import { articleImageAlts } from "@/data/articleImages";
import { sanitizeHtml } from "@/lib/sanitize";
import { computeReadingMinutes, enrichHeadings, extractFaq } from "@/lib/articleEnrichment";
import { BackToTop, ShareButtons } from "@/components/ArticleExtras";
import OddSpCalculator from "@/components/OddSpCalculator";
import { trackPageVisit } from "@/lib/vendor-visits.functions";
import { buildCanonical } from "@/lib/canonical-url";

export const Route = createFileRoute("/clanek/$slug")({
  loader: async ({ params }) => {
    const article = getArticle(params.slug);
    if (article) {
      const related = getRelatedArticles(article.slug, article.section, 4);
      return { article, related };
    }

    // Samodejno napisani članki so shranjeni v bazi, ne v statičnih datotekah.
    const { getPublishedDraft } = await import("@/lib/article-autowrite.functions");
    const draft = await getPublishedDraft({ data: { slug: params.slug } });
    if (!draft) throw notFound();

    const fromDraft: Article = {
      slug: draft.slug,
      title: draft.title,
      excerpt: draft.excerpt ?? "",
      content: draft.content ?? "",
      date: draft.published_at ?? new Date().toISOString(),
      section: (draft.section ?? "unikatne-vsebine") as Article["section"],
      featuredImage: draft.cover_url ?? undefined,
    };
    return { article: fromDraft, related: getRelatedArticles("", fromDraft.section, 4) };
  },
  head: ({ params, loaderData }) => {
    const url = buildCanonical(`/clanek/${params.slug}`);
    if (!loaderData)
      return {
        meta: [
          { title: "Članek ni na voljo — kupish.si" },
          { name: "robots", content: "noindex, follow" },
        ],
        links: [{ rel: "canonical", href: url }],
      };

    const a = loaderData.article;
    const imgAlt = articleImageAlts[a.slug] ?? a.title;
    const faq = extractFaq(a.content);
    const sectionPath = a.section === "unikatne-vsebine" ? "/predstavitev-prodajalca" : "/navdih";
    const sectionName = a.section === "unikatne-vsebine" ? "Unikatna predstavitev prodajalca" : "Navdih & zgodbe";
    const absImg = a.featuredImage
      ? (a.featuredImage.startsWith("http") ? a.featuredImage : `https://kupish.si${a.featuredImage}`)
      : undefined;
    const publisher = {
      "@type": "Organization",
      name: "kupish.si",
      url: "https://kupish.si/",
      logo: {
        "@type": "ImageObject",
        url: "https://kupish.si/favicon.ico",
      },
    };
    const toIso = (d: string) => {
      if (!d) return d;
      if (/^\d{4}-\d{2}-\d{2}T/.test(d)) return d;
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(d)) return d.replace(" ", "T") + "Z";
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      return d;
    };
    const publishedIso = toIso(a.date);
    const wordCount = a.content
      ? a.content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
      : undefined;
    const schemas: object[] = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: a.title.length <= 110 ? a.title : a.title.slice(0, 107).trimEnd() + "…",
        description: a.excerpt,
        datePublished: publishedIso,
        dateModified: publishedIso,
        inLanguage: "sl-SI",
        articleSection: sectionName,
        ...(wordCount ? { wordCount } : {}),
        author: {
          "@type": "Organization",
          name: "Uredništvo kupish.si",
          url: "https://kupish.si/o-nas",
        },
        publisher,
        ...(absImg
          ? {
              image: {
                "@type": "ImageObject",
                url: absImg,
                width: 1600,
                height: 900,
                caption: imgAlt,
              },
            }
          : {}),
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
      },

      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Domov", item: "https://kupish.si/" },
          { "@type": "ListItem", position: 2, name: sectionName, item: `https://kupish.si${sectionPath}` },
          { "@type": "ListItem", position: 3, name: a.title, item: url },
        ],
      },
    ];
    if (faq.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
    }
    return {
      meta: [
        { title: (`${a.title} — kupish.si`).length <= 60 ? `${a.title} — kupish.si` : (a.title.length <= 60 ? a.title : a.title.slice(0, 57).trimEnd() + "…") },
        { name: "description", content: a.excerpt },
        { property: "og:title", content: a.title },
        { property: "og:description", content: a.excerpt },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:site_name", content: "kupish.si" },
        { property: "og:locale", content: "sl_SI" },
        { name: "twitter:title", content: a.title },
        { name: "twitter:description", content: a.excerpt },
        ...(a.featuredImage
          ? [
              { property: "og:image", content: a.featuredImage.startsWith("http") ? a.featuredImage : `https://kupish.si${a.featuredImage}` },
              { property: "og:image:alt", content: imgAlt },
              { name: "twitter:image", content: a.featuredImage.startsWith("http") ? a.featuredImage : `https://kupish.si${a.featuredImage}` },
              { name: "twitter:image:alt", content: imgAlt },
              { name: "twitter:card", content: "summary_large_image" },
            ]
          : [{ name: "twitter:card", content: "summary" }]),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(a.featuredImage ? [{ rel: "preload", as: "image", href: a.featuredImage, fetchpriority: "high" }] : []),
      ],
      scripts: schemas.map((s) => ({
        type: "application/ld+json",
        children: JSON.stringify(s),
      })),
    };
  },
  notFoundComponent: () => (
    <section className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Članek ni najden</h1>
      <Link to="/" className="mt-6 inline-block text-accent">Nazaj na domačo stran</Link>
    </section>
  ),
  errorComponent: ({ error, reset }) => (
    <section className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-serif text-4xl">Napaka pri nalaganju članka</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
      <button onClick={reset} className="mt-6 text-accent">Poskusi znova</button>
    </section>
  ),
  component: Page,
});

function Page() {
  const { article, related } = Route.useLoaderData();
  const backTo = article.section === "unikatne-vsebine" ? "/predstavitev-prodajalca" : "/navdih";
  const backLabel = article.section === "unikatne-vsebine" ? "Unikatna predstavitev" : "Navdih & zgodbe";
  const sectionTag = article.section === "unikatne-vsebine" ? "Unikatna predstavitev" : "Navdih & zgodbe";

  const readingMin = computeReadingMinutes(article.content);
  // Odstrani morebitne ostanke WordPress/WPBakery kratkih kod iz uvožene vsebine
  const cleanedContent = article.content.replace(/\[\/?vc_[^\]]*\]/g, "");
  const { html: enrichedHtml, toc } = enrichHeadings(cleanedContent);
  const showToc = toc.length >= 3;
  const url = `https://kupish.si/clanek/${article.slug}`;

  const trackPage = useServerFn(trackPageVisit);
  useEffect(() => {
    void trackPage({
      data: { kind: "article", articleSlug: article.slug, path: `/clanek/${article.slug}` },
    }).catch(() => {});
  }, [article.slug, trackPage]);

  return (
    <article className="pb-24">

      {/* Hero z naslovno fotografijo v ozadju */}
      <header className="relative overflow-hidden">
        {article.featuredImage && (
          <>
            <img
              src={article.featuredImage}
              alt={articleImageAlts[article.slug] ?? article.title}
              title={article.title}
              width={1600}
              height={900}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover blur-[3px] scale-105"
            />
            <div className="absolute inset-0 bg-background/70 md:bg-background/60" aria-hidden="true" />
          </>
        )}
        <div className="relative mx-auto max-w-4xl px-6 pt-16 pb-12 md:pt-20 md:pb-16">
          {/* Visible breadcrumbs (matches BreadcrumbList JSON-LD) */}
          <nav aria-label="Drobtinice" className="text-xs text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li><Link to="/" className="hover:text-accent hover:underline">Domov</Link></li>
              <li aria-hidden="true">›</li>
              <li><Link to={backTo} className="hover:text-accent hover:underline">{backLabel}</Link></li>
              <li aria-hidden="true">›</li>
              <li className="text-foreground/80 line-clamp-1" aria-current="page">{article.title}</li>
            </ol>
          </nav>
          <h1 className="font-serif text-4xl md:text-6xl mt-6 leading-[1.05] tracking-tight">
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="mt-4 max-w-2xl text-base md:text-lg text-foreground/80">
              {article.excerpt}
            </p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span>Uredništvo kupish.si</span>
            <span aria-hidden="true">·</span>
            <time dateTime={article.date}>
              {new Date(article.date).toLocaleDateString("sl-SI", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
            <span aria-hidden="true">·</span>
            <span>{readingMin} min branja</span>
            <span aria-hidden="true">·</span>
            <Link to={backTo} className="text-accent hover:underline">{sectionTag}</Link>
          </div>
        </div>
      </header>


      {/* Body */}
      <div className="mx-auto max-w-3xl px-6 mt-14 md:mt-20">
        {showToc && (
          <nav
            aria-label="Kazalo članka"
            className="mb-10 rounded-lg border border-border bg-muted/30 p-6"
          >
            <div className="text-xs uppercase tracking-[0.18em] text-accent">V tem članku</div>
            <ol className="mt-4 space-y-2 list-decimal list-inside">
              {toc.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="text-brand hover:text-accent hover:underline">
                    {t.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="article-prose" dangerouslySetInnerHTML={{ __html: sanitizeHtml(enrichedHtml) }} />

        {article.slug === "odd-vs-popoldanski-sp" && <OddSpCalculator />}

        <ShareButtons url={url} title={article.title} />
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-5xl px-6 mt-24 pt-14 border-t border-border">
          <div className="eyebrow">Preberite več</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-3">
            Sorodni <span className=" text-accent">članki</span>
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {related.map((r: Article) => (
              <Link
                key={r.slug}
                to="/clanek/$slug"
                params={{ slug: r.slug }}
                className="group flex flex-col h-full border border-border rounded-lg overflow-hidden hover:border-accent transition-colors bg-card"
              >
                {r.featuredImage && (
                  <div className="aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src={r.featuredImage}
                      alt={articleImageAlts[r.slug] ?? r.title}
                      title={r.title}
                      width={640}
                      height={400}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-serif text-lg leading-snug text-left group-hover:text-accent text-foreground line-clamp-3 min-h-[4.2rem]">
                    {r.title}
                  </h3>
                  <span className="mt-auto pt-4 inline-block text-[0.65rem] uppercase tracking-[0.18em] text-accent text-left">
                    Preberi →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <BackToTop />
    </article>
  );
}
